import { useClientConfigModelStore } from '@/models/client-config/clientConfig';
import { useClientI18nModelStore } from '@/models/client-i18n/clientI18n';
import { useDocsDataModelStore } from '@/models/docs-data/docsData';
import { useEnvConfigModelStore } from '@/models/env-config/envConfig';
import type { RequestDocsSiteCloneDataType } from '@/services/docs-site-clone/requestDocsSiteClone';
import { requestDocsSiteClone } from '@/services/docs-site-clone/requestDocsSiteClone';
import { DocsSiteVisitType } from '@/shared/constant/DocsIdTypeData';
import { OnlineType } from '@/shared/constant/OnlineType';
import { usePersistFn } from 'ahooks';
import { Button, notification } from 'aui';
import React, { useEffect } from 'react';

export function useCloneProject() {
  const [isCloning, setIsCloning] = React.useState(false);

  const clientI18n = useClientI18nModelStore((x) => x.clientI18n);
  const title = useDocsDataModelStore((x) => x.docsBaseConfig.title) || '';
  const onlineId = useDocsDataModelStore((x) => x.docsIdTypeData.onlineId);
  const onlineType = useDocsDataModelStore((x) => x.docsIdTypeData.onlineType);
  const teamId = useDocsDataModelStore((x) => x.docsIdTypeData.teamId);
  const projectId = useDocsDataModelStore((x) => x.docsIdTypeData.projectId);
  const branchId = useDocsDataModelStore((x) => {
    if (x.docsIdTypeData.onlineType === OnlineType.SharedDoc) {
      return undefined;
    }
    return x.docsIdTypeData.branchId;
  });
  const subdirectory = useDocsDataModelStore((x) => {
    if (x.docsIdTypeData.onlineType === OnlineType.SharedDoc) {
      return '';
    }
    return x.docsIdTypeData.subdirectory;
  });
  const visitType = useDocsDataModelStore((x) => {
    if (x.docsIdTypeData.onlineType === OnlineType.SharedDoc) {
      return DocsSiteVisitType.WithId;
    }
    return x.docsIdTypeData.visitType;
  });
  const envConfig = useEnvConfigModelStore((x) => x);
  const appWebUrl = useClientConfigModelStore((x) => x.appWebUrl);
  const src = React.useMemo(() => {
    return IS_PDV
      ? `${appWebUrl}/web/online-share/clone?onlineShareName=${encodeURIComponent(title)}&teamId=${teamId}`
      : `${appWebUrl}/online-share/clone?onlineShareName=${encodeURIComponent(title)}&teamId=${teamId}`;
  }, [appWebUrl, teamId, title]);

  const notificationClose = usePersistFn((myNotification: any, key: any, cloneProjectId: any) => {
    myNotification.close(key);
    window.open(
      IS_PDV
        ? `${appWebUrl}/web/project/${cloneProjectId}`
        : `${appWebUrl}/project/${cloneProjectId}`,
    );
  });

  const openCloneSucceedNotification = usePersistFn(
    (teamName: string, cloneProjectId: number | undefined) => {
      const key = `open${Date.now()}`;
      const btn = (
        <Button type="primary" onClick={() => notificationClose(notification, key, cloneProjectId)}>
          {clientI18n('projectDetail.label.lookDetailButton')}
        </Button>
      );

      notification.open({
        message: clientI18n('projectDetail.label.lookDetailButton'),
        description: (
          <span
            dangerouslySetInnerHTML={{
              __html: clientI18n('onlineShareHasBeenCopyToTeam', {
                onlineShareName: title,
                teamName,
              }),
            }}
          />
        ),
        duration: null,
        btn,
        key,
      });
    },
  );

  const handleRef = React.useRef<Window | null>(null);
  const cloneProject = usePersistFn(() => {
    handleRef.current?.close();

    const width = 560;
    const height = 640;
    const screenWidth = window.screen.width;
    const screenHeight = window.screen.height;
    const left = (screenWidth - width) / 2;
    const top = (screenHeight - height) / 2;

    const features = `width=${width},height=${height},left=${left},top=${top}`;
    handleRef.current = window.open(src, 'webCloneWindow', features);
  });

  useEffect(() => {
    const handleMessage = async (event: MessageEvent) => {
      if (event.data?.type === 'HANDLE_CLONE') {
        const { teamId: selectedTeamId, userId } = event.data.payload;
        if (!onlineId) {
          return;
        }
        try {
          handleRef.current?.close();
          setIsCloning(true);
          const requestData: RequestDocsSiteCloneDataType = {
            teamId: selectedTeamId,
            userId,
            id: onlineId,
            projectId,
            branchId,
          };

          if (onlineType === OnlineType.DocsSite) {
            requestData.branchId = branchId;
          }

          const [, result] = await requestDocsSiteClone(
            requestData,
            onlineType,
            envConfig,
            clientI18n,
            visitType,
            subdirectory,
          );
          if (result) {
            openCloneSucceedNotification(result.teamName, result.projectId);
          }
        } catch (error) {
          //
        } finally {
          setIsCloning(false);
        }
      }
    };

    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [
    onlineId,
    branchId,
    onlineType,
    projectId,
    envConfig,
    clientI18n,
    visitType,
    subdirectory,
    openCloneSucceedNotification,
  ]);

  React.useEffect(() => {
    return () => {
      handleRef.current?.close();
    };
  }, []);

  return { isCloning, cloneProject };
}
